import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  Star, 
  Phone, 
  Mail, 
  MapPin, 
  Award, 
  Users, 
  Clock, 
  ThumbsUp, 
  CheckCircle,
  Droplets, // For driveways
  Home, // For houses
  Umbrella, // For roof softwashing
  Fence, // For fences
  Grid, // For paver installation
  ShieldCheck // For paver sealing
} from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

// Import real photos
import heroImage from '../assets/images/hero.jpg';
import patternBackground from '../assets/images/cta-bg.jpg';
import afterDriveway from '../assets/images/before-fence.jpg';
import beforeDriveway from '../assets/images/after-house.jpg';
import beforeHouse from '../assets/images/before-house.jpg';
import afterHouse from '../assets/images/before-driveway.jpg';
import beforeFence from '../assets/images/service-houses.jpg';
import afterFence from '../assets/images/after-fence.jpg';

// Before/After image pairs
const beforeAfterImages = [
  {
    id: 1,
    before: beforeDriveway,
    after: afterDriveway,
    title: "Driveway Cleaning"
  },
  {
    id: 2,
    before: beforeHouse,
    after: afterHouse,
    title: "House Washing"
  },
  {
    id: 3,
    before: beforeFence,
    after: afterFence,
    title: "Fence Restoration"
  }
];

// Before/After Slider Component
const BeforeAfterSlider = ({ beforeImage, afterImage, title }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [showAfter, setShowAfter] = useState(false);
  const sliderRef = useRef(null);

  const handleMove = (e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = (x / rect.width) * 100;
    setSliderPosition(Math.min(100, Math.max(0, position)));
  };

  const handleTouchMove = (e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const position = (x / rect.width) * 100;
    setSliderPosition(Math.min(100, Math.max(0, position)));
    e.preventDefault();
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-lg">

      {/* Mobile: toggle button */}
      <div className="md:hidden">
        <div className="h-64 relative">
          <img
            src={showAfter ? afterImage : beforeImage}
            alt={showAfter ? "After cleaning" : "Before cleaning"}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          {/* Before / After pill toggle */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 flex rounded-full overflow-hidden shadow-lg z-20 border border-white/30">
            <button
              onClick={() => setShowAfter(false)}
              className={`px-5 py-1.5 text-sm font-semibold transition-colors ${!showAfter ? 'bg-forest-700 text-white' : 'bg-black/40 text-white/80'}`}
            >
              Before
            </button>
            <button
              onClick={() => setShowAfter(true)}
              className={`px-5 py-1.5 text-sm font-semibold transition-colors ${showAfter ? 'bg-forest-700 text-white' : 'bg-black/40 text-white/80'}`}
            >
              After
            </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 z-20">
            <p className="text-white font-bold">{title}</p>
          </div>
        </div>
      </div>

      {/* Desktop: drag slider */}
      <div
        className="hidden md:block h-64 bg-gray-200 relative select-none cursor-ew-resize"
        ref={sliderRef}
        onMouseMove={handleMove}
        onTouchMove={handleTouchMove}
      >
        <img
          src={afterImage}
          alt="After cleaning"
          className="w-full h-full object-cover absolute top-0 left-0"
          loading="lazy"
          decoding="async"
        />
        <div
          className="absolute top-0 left-0 h-full overflow-hidden"
          style={{ width: `${sliderPosition}%` }}
        >
          <img
            src={beforeImage}
            alt="Before cleaning"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            style={{
              width: `${100 / (sliderPosition / 100)}%`,
              maxWidth: sliderPosition > 0 ? `${10000 / sliderPosition}%` : '1000%'
            }}
          />
        </div>
        <div
          className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize shadow-md z-10"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center">
            <div className="text-forest-700 text-xs">↔</div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 z-20">
          <p className="text-white font-bold">{title}</p>
        </div>
      </div>

    </div>
  );
};

const HomePage = () => {
  // Services data with Lucide icons
  const services = [
    { 
      id: 'driveways', 
      title: 'Driveways', 
      description: 'Restore your driveway to like-new condition with our professional pressure washing services.',
      icon: <Droplets className="h-10 w-10 text-forest-600" />
    },
    { 
      id: 'houses', 
      title: 'Houses', 
      description: 'Enhance your home\'s curb appeal with our complete exterior house washing.',
      icon: <Home className="h-10 w-10 text-forest-600" />
    },
    { 
      id: 'roof-softwashing', 
      title: 'Roof Softwashing', 
      description: 'Gently remove algae, mold, and stains from your roof with our specialized softwashing technique.',
      icon: <Umbrella className="h-10 w-10 text-forest-600" />
    },
    { 
      id: 'fences', 
      title: 'Fences', 
      description: 'Revitalize your fences to restore their original beauty and extend their lifespan.',
      icon: <Fence className="h-10 w-10 text-forest-600" />
    },
    { 
      id: 'paver-installation', 
      title: 'Paver Installation', 
      description: 'Transform your outdoor spaces with professional paver installation services.',
      icon: <Grid className="h-10 w-10 text-forest-600" />
    },
    { 
      id: 'paver-sealing', 
      title: 'Paver Sealing', 
      description: 'Protect your investment with our paver sealing services that enhance color and durability.',
      icon: <ShieldCheck className="h-10 w-10 text-forest-600" />
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      id: 1,
      name: 'Sarah Johnson',
      rating: 5,
      text: 'Floboys did an amazing job on my driveway and sidewalk. They look brand new! Professional, on-time, and reasonably priced. Highly recommend!'
    },
    {
      id: 2,
      name: 'Michael Davis',
      rating: 5,
      text: 'I was blown away by how good my house looks after their cleaning. The team was courteous and thorough. Will definitely use them again!'
    },
    {
      id: 3,
      name: 'Jennifer Wilson',
      rating: 5,
      text: 'These guys know what they\'re doing with roof cleaning. No damage, just a perfectly clean roof that looks brand new. Very impressed!'
    }
  ];

  // FAQ data
  const faqs = [
    {
      id: 1,
      question: 'How often should I have my house pressure washed?',
      answer: 'For most homes, we recommend pressure washing once a year to maintain your home\'s appearance and prevent damage from mold, mildew, and other contaminants.'
    },
    {
      id: 2,
      question: 'Is pressure washing safe for all surfaces?',
      answer: 'Not all surfaces should be cleaned with high-pressure washing. We use different techniques including soft washing for more delicate surfaces like roofs and certain siding types.'
    },
    {
      id: 3,
      question: 'How long does a typical pressure washing job take?',
      answer: 'The time varies depending on the size and condition of the area being cleaned. Most residential jobs can be completed in 2-4 hours.'
    },
    {
      id: 4,
      question: 'Do I need to be home during the service?',
      answer: 'As long as we have access to the areas being cleaned and water sources, you don\'t need to be present during the service.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Enhanced Hero Section with Animated Elements */}
        <div className="relative bg-forest-700">
          <div className="absolute inset-0 overflow-hidden">
            <img 
              src={heroImage}
              alt="Pressure washing service" 
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-forest-900 via-forest-700 to-forest-800 opacity-70"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 py-24 sm:px-6 lg:px-8 lg:py-32 text-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Professional Pressure Washing Services
            </h1>
            <p className="mt-6 text-xl text-forest-100 max-w-3xl mx-auto">
              Revitalize your property with our expert pressure washing solutions. 
              From driveways to houses, we restore surfaces to their original beauty.
            </p>
            
            {/* Added Feature Badges */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-lg p-4 flex flex-col items-center">
                <Award className="h-8 w-8 text-white mb-2" />
                <span className="text-white font-medium">Licensed & Insured</span>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-lg p-4 flex flex-col items-center">
                <Users className="h-8 w-8 text-white mb-2" />
                <span className="text-white font-medium">Experienced Team</span>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-lg p-4 flex flex-col items-center">
                <Clock className="h-8 w-8 text-white mb-2" />
                <span className="text-white font-medium">On-Time Service</span>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-lg p-4 flex flex-col items-center">
                <ThumbsUp className="h-8 w-8 text-white mb-2" />
                <span className="text-white font-medium">Satisfaction Guaranteed</span>
              </div>
            </div>
            
            <div className="mt-10 flex justify-center">
              <Link 
                to="/contact" 
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-forest-700 bg-white hover:bg-forest-50 transition duration-150 shadow-lg"
              >
                Get Free Estimate
                <ChevronRight className="ml-2" size={16} />
              </Link>
              <Link 
                to="/services" 
                className="ml-4 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-forest-600 hover:bg-forest-500 transition duration-150 shadow-lg"
              >
                Our Services
                <ChevronRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
        </div>

        {/* Add Angled Separator */}
        <div className="h-16 bg-forest-700 relative">
          <svg className="absolute bottom-0 w-full h-16 text-gray-50" preserveAspectRatio="none" viewBox="0 0 100 100">
            <polygon points="0,100 100,0 100,100" className="fill-current"></polygon>
          </svg>
        </div>

        {/* Before/After Gallery with Introduction Text */}
        <div className="py-16 bg-gray-50 relative">
          <div className="absolute inset-0 opacity-5 bg-repeat" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23325749' fill-opacity='0.05' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E\")" }}></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <span className="inline-block px-3 py-1 text-sm font-semibold text-forest-700 bg-forest-100 rounded-full mb-3">Transformation Showcase</span>
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                See the Difference
              </h2>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
                Our professional pressure washing services transform dirty, stained surfaces back to their original beauty. 
                Drag the slider to see the dramatic before and after results.
              </p>
            </div>
            
            {/* Before/After Sliders */}
            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {beforeAfterImages.map(image => (
                <BeforeAfterSlider 
                  key={image.id}
                  beforeImage={image.before}
                  afterImage={image.after}
                  title={image.title}
                />
              ))}
            </div>
            
            <div className="mt-10 text-center">
              <Link
                to="/services"
                className="inline-flex items-center px-5 py-2 border border-transparent text-base font-medium rounded-md text-forest-700 bg-forest-100 hover:bg-forest-200 shadow"
              >
                View Our Services
                <ChevronRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
        </div>

        {/* Wave Pattern Separator */}
        <div className="bg-forest-100 h-24 relative">
          <svg className="absolute top-0 w-full h-24 fill-current text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
          </svg>
        </div>

        {/* Services Section with Professional Icons */}
        <div className="py-16 bg-forest-100 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-3 py-1 text-sm font-semibold text-forest-700 bg-white rounded-full mb-3">Our Expertise</span>
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Professional Services
              </h2>
              <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto">
                We offer comprehensive pressure washing solutions to keep your property looking its best year-round.
              </p>
            </div>
            
            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {services.map((service) => (
                <div key={service.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-forest-50 w-16 h-16 flex items-center justify-center rounded-full">
                        {service.icon}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 ml-4">{service.title}</h3>
                    </div>
                    <p className="mt-2 text-gray-600">{service.description}</p>
                    <Link 
                      to={`/services#${service.id}`} 
                      className="mt-4 inline-flex items-center text-forest-600 font-medium hover:text-forest-500"
                    >
                      Learn more
                      <ChevronRight className="ml-1" size={16} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <Link 
                to="/services" 
                className="inline-flex items-center px-5 py-2 border border-transparent text-base font-medium rounded-md text-white bg-forest-600 hover:bg-forest-500 shadow-md"
              >
                View All Services
                <ChevronRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
        </div>

        {/* Testimonials Section with Card Design */}
        <div className="py-16 bg-white relative">
          <div className="absolute inset-0 opacity-5 bg-repeat" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23325749' fill-opacity='0.05' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3Ccircle cx='13' cy='13' r='1'/%3E%3C/g%3E%3C/svg%3E\")" }}></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <span className="inline-block px-3 py-1 text-sm font-semibold text-forest-700 bg-forest-100 rounded-full mb-3">Client Feedback</span>
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                What Our Customers Say
              </h2>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
                Don&apos;t just take our word for it. Here&apos;s what our satisfied customers have to say about our services.
              </p>
            </div>
            
            {/* Testimonial card grid */}
            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={20}
                        className={i < testimonial.rating ? "text-yellow-400 fill-current" : "text-gray-300"}
                      />
                    ))}
                  </div>
                  <p className="mt-4 text-gray-600 italic">"{testimonial.text}"</p>
                  <div className="mt-6 pt-6 border-t border-gray-100 flex items-center">
                    <div className="bg-forest-100 rounded-full w-12 h-12 flex items-center justify-center text-forest-700 font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <p className="ml-3 font-medium text-gray-900">{testimonial.name}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Add testimonial CTA */}
            <div className="mt-12 bg-forest-50 rounded-lg p-8 text-center shadow-inner">
              <h3 className="text-2xl font-bold text-gray-900">Join our satisfied customers!</h3>
              <p className="mt-3 text-gray-600">Experience the Floboys difference for yourself. Get your free estimate today.</p>
              <Link 
                to="/contact" 
                className="mt-6 inline-flex items-center px-5 py-2 border border-transparent text-base font-medium rounded-md text-white bg-forest-600 hover:bg-forest-700 shadow"
              >
                Get Your Free Estimate
                <ChevronRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ Section with Improved Design */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-3 lg:gap-8">
              <div className="lg:col-span-1">
                <div>
                  <span className="inline-block px-3 py-1 text-sm font-semibold text-forest-700 bg-forest-100 rounded-full mb-3">FAQ</span>
                  <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                    Common Questions
                  </h2>
                  <p className="mt-4 text-lg text-gray-500">
                    Find answers to frequently asked questions about our pressure washing services and process.
                  </p>
                  <div className="mt-8">
                    <Link 
                      to="/contact" 
                      className="inline-flex items-center px-5 py-2 border border-transparent text-base font-medium rounded-md text-white bg-forest-600 hover:bg-forest-500 shadow"
                    >
                      Ask Us a Question
                      <ChevronRight className="ml-2" size={16} />
                    </Link>
                  </div>
                </div>
              </div>
              
              <div className="mt-12 lg:mt-0 lg:col-span-2">
                <dl className="space-y-6">
                  {faqs.map((faq) => (
                    <div key={faq.id} className="bg-white shadow rounded-lg p-6 border-l-4 border-forest-600">
                      <dt className="text-lg font-bold text-gray-900 flex items-start">
                        <span className="flex-shrink-0 mr-2">
                          <CheckCircle className="h-6 w-6 text-forest-500" />
                        </span>
                        {faq.question}
                      </dt>
                      <dd className="mt-2 text-gray-600 ml-8">{faq.answer}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section with Enhanced Design */}
        <div className="bg-forest-700 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src={patternBackground}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8 lg:py-24 relative">
            <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-xl shadow-xl overflow-hidden">
              <div className="lg:grid lg:grid-cols-2 lg:gap-8">
                <div className="px-6 py-10 lg:p-12">
                  <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                    Ready to Transform Your Property?
                  </h2>
                  <p className="mt-4 text-lg text-forest-100">
                    Get a free, no-obligation estimate for your pressure washing needs. 
                    We&apos;ll help restore your property&apos;s beauty and protect your investment.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-forest-700 bg-white hover:bg-gray-100 shadow-lg"
                    >
                      Get Your Free Estimate
                      <ChevronRight className="ml-2" size={16} />
                    </Link>
                    
                    <a href="tel:+14079063215"
                      className="inline-flex items-center justify-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-forest-600 shadow-lg"
                    >
                      <Phone size={18} className="mr-2" /> Call Now
                    </a>
                  </div>
                </div>
                <div className="px-6 py-10 lg:p-12 bg-white bg-opacity-10">
                  <div className="space-y-6">
                    <div className="flex items-center p-4 bg-white bg-opacity-20 rounded-lg">
                      <div className="flex-shrink-0">
                        <MapPin className="h-8 w-8 text-white" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-white">Serving All Local Areas</h3>
                        <p className="text-forest-100">Professional, reliable service you can count on</p>
                      </div>
                    </div>
                    <div className="flex items-center p-4 bg-white bg-opacity-20 rounded-lg">
                      <div className="flex-shrink-0">
                        <Phone className="h-8 w-8 text-white" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-white">Call For a Quote</h3>
                        <p className="text-forest-100">(407) 906-3215</p>
                      </div>
                    </div>
                    <div className="flex items-center p-4 bg-white bg-opacity-20 rounded-lg">
                      <div className="flex-shrink-0">
                        <Mail className="h-8 w-8 text-white" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-white">Email Us</h3>
                        <p className="text-forest-100">floboyspressurewashing@gmail.com</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;